import { Chess } from "chess.js";
import { Action, type Action as ActionType } from "../shared/actions";
import {
  activeContext,
  colorName,
  DEFAULT_POSITION,
  gamePgn,
  opposite,
  positionStatus,
  replay,
  resolveMove,
  uci,
} from "../shared/chess";
import {
  patternLabels,
  type AnalysisLine,
  type AppState,
  type Evidence,
  type Exercise,
  type Game,
  type Pattern,
} from "../shared/types";
import type { EngineProvider } from "./engine-types";
import { openingIdeas, mirrorPerspective, mirrorMove } from "./studios";
import { curriculum, lessons } from "./curriculum";
// A request seed gives optimistic and authoritative actions the same object IDs.
// Each working copy owns its counter; concurrent requests never share mutable state.
const identities = new WeakMap<AppState, { seed: string; next: number }>();
const uid = (state: AppState) => {
  const identity = identities.get(state);
  if (!identity) return crypto.randomUUID();
  const suffix = (
    BigInt(`0x${identity.seed.slice(-12)}`) ^ BigInt(++identity.next)
  )
    .toString(16)
    .padStart(12, "0");
  return `${identity.seed.slice(0, -12)}${suffix}`;
};
const now = () => Date.now();
export function message(
  state: AppState,
  text: string,
  role: "user" | "assistant" | "system" = "assistant",
  reference?: { gameId: string; ply: number; variationId?: string },
) {
  state.messages.push({
    id: uid(state),
    role,
    text,
    at: now(),
    ...(reference ? { reference } : {}),
  });
  if (state.messages.length > 300) state.messages = state.messages.slice(-300);
}
export function createGame(
  state: AppState,
  action: Extract<ActionType, { type: "game.create" }>,
): Game {
  if (
    action.aiColor &&
    action.humanColor &&
    action.aiColor === action.humanColor
  )
    throw new Error("The coach and player must have different colors.");
  const humanColor = action.aiColor
    ? opposite(action.aiColor)
    : (action.humanColor ?? state.preferences.side);
  const chess = new Chess(action.fen || DEFAULT_POSITION);
  // Reject positions whose nonmoving king is already attacked: these cannot arise legally.
  const otherKing = chess
    .board()
    .flat()
    .find((p) => p?.type === "k" && p.color !== chess.turn());
  if (otherKing && chess.isAttacked(otherKing.square, chess.turn()))
    throw new Error(
      "Invalid position: the side that just moved left its king in check.",
    );
  const openings = {
    italian: ["e4", "e5", "Nf3", "Nc6", "Bc4"],
    sicilian: ["e4", "c5"],
    "queens-gambit": ["d4", "d5", "c4"],
    "ruy-lopez": ["e4", "e5", "Nf3", "Nc6", "Bb5"],
  };
  const moves: string[] = [];
  if (action.opening) {
    if (action.fen)
      throw new Error("Choose an opening or a custom position, not both.");
    for (const move of openings[action.opening])
      moves.push(uci(chess.move(move)));
  }
  const game: Game = {
    id: uid(state),
    title: action.mode === "play" ? "Training game" : "Analysis",
    createdAt: now(),
    updatedAt: now(),
    initialFen: action.fen || DEFAULT_POSITION,
    moves,
    revision: 0,
    mode: action.mode,
    humanColor,
    orientation: humanColor,
    difficulty: action.difficulty,
    coaching: action.coaching ?? state.preferences.coaching,
    hintFirst: action.hintFirst ?? state.preferences.hintFirst,
    liveReview: action.liveReview ?? state.preferences.liveReview ?? "mistakes",
    liveReviewFrom: moves.length,
    result: "*",
    pinned: false,
    headers: {
      White: humanColor === "w" ? "You" : "Cretchess",
      Black: humanColor === "b" ? "You" : "Cretchess",
      Date: new Date().toISOString().slice(0, 10).replaceAll("-", "."),
      TimeControl: action.minutes
        ? `${action.minutes * 60}+${action.increment}`
        : "-",
      ...(action.opening ? { Opening: action.opening } : {}),
    },
    clock: {
      initial: action.minutes * 60000,
      increment: action.increment * 1000,
      w: action.minutes * 60000,
      b: action.minutes * 60000,
      startedAt: action.mode === "play" && action.minutes > 0 ? now() : null,
    },
  };
  finishPosition(game);
  state.games.unshift(game);
  state.activeGameId = game.id;
  state.activeVariationId = null;
  state.viewPly = null;
  state.workspace = "board";
  state.lesson = null;
  state.studio = null;
  state.teaching = null;
  state.analysis = null;
  return game;
}
export function finishPosition(game: Game) {
  const status = positionStatus(replay(game));
  if (status.ended) {
    game.result = status.result;
    game.endReason = status.label;
    game.clock.startedAt = null;
  }
}
export function tickClock(game: Game, at = now()): boolean {
  if (
    !game.clock.initial ||
    game.clock.startedAt === null ||
    game.result !== "*"
  )
    return false;
  const chess = replay(game);
  const color = chess.turn();
  game.clock[color] = Math.max(
    0,
    game.clock[color] - Math.max(0, at - game.clock.startedAt),
  );
  game.clock.startedAt = at;
  if (!game.clock[color]) {
    const opponentPieces = chess
      .board()
      .flat()
      .filter((p) => p && p.color !== color);
    // Conservative exact subset of timeout draws: a bare king can never mate.
    game.result =
      opponentPieces.length === 1 ? "1/2-1/2" : color === "w" ? "0-1" : "1-0";
    game.endReason =
      game.result === "1/2-1/2"
        ? "Draw · timeout against a bare king"
        : `${colorName(opposite(color))} wins on time`;
    game.clock.startedAt = null;
    game.revision++;
    return true;
  }
  return false;
}
function requireGame(state: AppState) {
  const game = state.games.find((g) => g.id === state.activeGameId);
  if (!game) throw new Error("Open a game first.");
  return game;
}
function guardAnalysis(state: AppState) {
  const game = requireGame(state);
  if (
    game.mode === "play" &&
    game.result === "*" &&
    game.coaching === "postgame"
  )
    throw new Error(
      "Post-game review is enabled. Analysis will be available when this game ends.",
    );
}
function clearAnalysis(state: AppState) {
  state.analysis = null;
  state.comparison = [];
  state.annotations = null;
}
export function applyMove(state: AppState, input: string, byEngine = false) {
  const { game, variation, record, chess } = activeContext(state);
  if (!game || !record) throw new Error("Open a board first.");
  if (state.viewPly !== null && state.viewPly !== record.moves.length)
    throw new Error(
      "This is a past position. Open a What-If branch to explore it.",
    );
  if (!variation && game.result !== "*")
    throw new Error(
      "This game has ended. Open a What-If branch to continue exploring.",
    );
  if (positionStatus(chess).ended)
    throw new Error("This position has no further play.");
  if (
    !variation &&
    game.mode === "play" &&
    (chess.turn() !== game.humanColor) !== byEngine
  )
    throw new Error(
      byEngine ? "It is your turn." : "It is the opponent’s turn.",
    );
  if (!variation && tickClock(game)) {
    clearAnalysis(state);
    return game.endReason!;
  }
  const move = chess.move(resolveMove(chess, input));
  record.moves.push(uci(move));
  record.revision++;
  if (!variation) {
    game.updatedAt = now();
    if (game.clock.startedAt !== null)
      game.clock[move.color] += game.clock.increment;
    finishPosition(game);
  }
  state.viewPly = null;
  clearAnalysis(state);
  return `${move.san} played. ${variation ? positionStatus(chess).label : (game.endReason ?? positionStatus(chess).label)}.`;
}
function score(line: AnalysisLine, color: "w" | "b", sideToMove = color) {
  return (
    (line.cp ??
      (line.mate === 0
        ? -10000 * (sideToMove === "w" ? 1 : -1)
        : Math.sign(line.mate ?? 0) *
          (10000 - Math.abs(line.mate ?? 0) * 10))) * (color === "w" ? 1 : -1)
  );
}
export async function inspectMove(
  state: AppState,
  game: Game,
  ply: number,
  provider: EngineProvider,
  signal?: AbortSignal,
): Promise<Evidence | null> {
  if (ply < 1 || ply > game.moves.length)
    throw new Error("There is no move to review yet.");
  const beforeChess = replay(game, ply - 1);
  const fen = beforeChess.fen();
  const color = beforeChess.turn();
  const before = (
    await provider.analyze({
      initialFen: game.initialFen,
      moves: game.moves.slice(0, ply - 1),
      milliseconds: 350,
      signal,
    })
  ).lines[0];
  const after = (
    await provider.analyze({
      initialFen: game.initialFen,
      moves: game.moves.slice(0, ply),
      milliseconds: 350,
      signal,
    })
  ).lines[0];
  const afterChess = replay(game, ply);
  const terminal = positionStatus(afterChess);
  const afterLine =
    after ??
    (terminal.ended
      ? {
          moves: [],
          san: [],
          cp: afterChess.isCheckmate() ? null : 0,
          mate: afterChess.isCheckmate() ? 0 : null,
          depth: 0,
        }
      : null);
  if (!before || !afterLine || !before.moves[0]) return null;
  const loss = Math.max(
    0,
    score(before, color) - score(afterLine, color, afterChess.turn()),
  );
  const best = beforeChess.move(before.moves[0]);
  const pattern: Pattern =
    before.mate !== null &&
    score(before, color) > 0 &&
    score(afterLine, color, afterChess.turn()) < 9000
      ? "missed-mate"
      : afterLine.mate !== null &&
          score(afterLine, color, afterChess.turn()) < -9000
        ? "allowed-mate"
        : best.captured
          ? "missed-capture"
          : "lost-advantage";
  const evidence: Evidence = {
    id: `${game.id}:${ply}`,
    gameId: game.id,
    ply,
    fen,
    played: game.moves[ply - 1],
    best: before.moves[0],
    loss,
    pattern,
    before,
    after: afterLine,
    at: now(),
    dismissed: false,
    note: "",
  };
  if (loss >= 150 && state.preferences.memory) {
    const existing = state.evidence.find((e) => e.id === evidence.id);
    if (existing) {
      evidence.dismissed = existing.dismissed;
      evidence.note = existing.note;
      state.evidence[state.evidence.indexOf(existing)] = evidence;
    } else state.evidence.push(evidence);
  }
  return evidence;
}
function evidenceText(evidence: Evidence, hintFirst: boolean) {
  const chess = new Chess(evidence.fen);
  const played = chess.move(evidence.played).san;
  if (evidence.loss < 80)
    return `${played} is close to the engine’s preference in this limited search. There is no clear evidence here that it was a mistake.`;
  const theme =
    evidence.pattern === "allowed-mate"
      ? "Check the opponent’s forcing checks and your king’s escape squares."
      : evidence.pattern === "missed-mate"
        ? "Look for a forcing check and count the king’s escape squares."
        : evidence.pattern === "missed-capture"
          ? "Compare the forcing captures before committing to a quiet move."
          : "Compare at least two candidate moves, then look for the opponent’s strongest reply.";
  const consequence =
    evidence.before.mate !== null || evidence.after.mate !== null
      ? "changed the mating prospects in the engine’s search"
      : `gave up approximately ${(evidence.loss / 100).toFixed(1)} pawns of engine evaluation`;
  return `${played} ${consequence} at depth ${Math.min(evidence.before.depth, evidence.after.depth)}. ${theme}${hintFirst ? "" : ` The engine preferred ${evidence.before.san.slice(0, 4).join(" ")}.`} This is a limited search, not proof of a forced result.`;
}
async function makeExercise(
  state: AppState,
  data: {
    fen: string;
    title: string;
    concept: string;
    source: Exercise["source"];
    evidenceId?: string;
    gameId?: string;
    variant?: "original" | "perspective";
  },
  provider: EngineProvider,
  signal?: AbortSignal,
) {
  const existing = state.exercises.find(
    (e) => e.fen === data.fen && e.source === data.source,
  );
  if (existing) return existing;
  const chess = new Chess(data.fen);
  if (positionStatus(chess).ended)
    throw new Error("This position has already ended. Choose an earlier move.");
  const result = await provider.analyze({
    initialFen: data.fen,
    milliseconds: 700,
    multiPV: 2,
    signal,
  });
  const line = result.lines[0];
  if (!line?.moves.length)
    throw new Error(
      "The engine could not validate an exercise for this position.",
    );
  if (data.source === "curriculum" && !line.san[0].endsWith("#"))
    throw new Error(
      "This foundation position did not pass its mate-in-one validation.",
    );
  const exercise: Exercise = {
    ...data,
    id: uid(state),
    solution: line.moves,
    san: line.san,
    depth: line.depth,
    attempts: [],
    dueAt: now(),
  };
  state.exercises.push(exercise);
  return exercise;
}
export async function executeActions(
  source: AppState,
  rawActions: unknown[],
  provider: EngineProvider,
  signal?: AbortSignal,
  seed?: string,
): Promise<{ state: AppState; results: string[] }> {
  const actions = rawActions.map((a) => Action.parse(a));
  const state = structuredClone(source);
  if (seed) identities.set(state, { seed, next: 0 });
  const results: string[] = [];
  for (const a of actions) {
    signal?.throwIfAborted();
    const result = await executeOne(state, a, provider, signal);
    results.push(result);
  }
  signal?.throwIfAborted();
  return { state, results };
}
async function executeOne(
  state: AppState,
  a: ActionType,
  provider: EngineProvider,
  signal?: AbortSignal,
): Promise<string> {
  switch (a.type) {
    case "workspace.present":
      if (a.workspace === "board" && !state.activeGameId)
        createGame(
          state,
          Action.parse({ type: "game.create" }) as Extract<
            ActionType,
            { type: "game.create" }
          >,
        );
      state.workspace = a.workspace;
      return `${a.workspace === "board" ? "Your board" : a.workspace} is open.`;
    case "workspace.dismiss":
      state.workspace = null;
      return "Workspace saved. Everything is here when you return.";
    case "workspace.restore":
      if (!state.activeGameId)
        throw new Error("There is no saved workspace yet.");
      state.workspace = "board";
      return "Your exact position is restored.";
    case "game.create": {
      const game = createGame(state, a);
      return game.mode === "play"
        ? `You’re playing ${colorName(game.humanColor)}; Cretchess plays ${colorName(opposite(game.humanColor))}. ${game.difficulty} difficulty, ${game.coaching} coaching${game.hintFirst ? ", hints before answers" : ""}. ${game.clock.initial ? `${a.minutes}+${a.increment} time control` : "No clock—take your time"}.`
        : "Your analysis board is ready. You can move either side.";
    }
    case "game.configure": {
      const game = requireGame(state);
      if ((a.humanColor || a.mode) && game.moves.length)
        throw new Error(
          "Create a new game to change the players or game mode after moves have been played.",
        );
      const { type, ...changes } = a;
      Object.assign(game, changes);
      game.revision++;
      clearAnalysis(state);
      return "Game settings updated.";
    }
    case "game.makeMove":
      return applyMove(state, a.move);
    case "game.undo": {
      const { game, variation, record } = activeContext(state);
      if (!game || !record || !record.moves.length)
        throw new Error("There is no move to take back.");
      if (!variation && game.clock.initial)
        throw new Error(
          "Takebacks are disabled in timed games. Explore a branch instead.",
        );
      record.moves.pop();
      if (
        !variation &&
        game.mode === "play" &&
        replay(game).turn() !== game.humanColor &&
        game.moves.length
      )
        game.moves.pop();
      record.revision++;
      if (record.liveReviewFrom !== undefined)
        record.liveReviewFrom = Math.min(record.liveReviewFrom, record.moves.length);
      state.moveReviews = state.moveReviews?.filter(
        (review) =>
          review.gameId !== game.id ||
          review.variationId !== (variation?.id ?? null) ||
          review.ply <= record.moves.length,
      );
      if (!variation) {
        game.result = "*";
        delete game.endReason;
        state.evidence = state.evidence.filter(
          (e) => e.gameId !== game.id || e.ply <= game.moves.length,
        );
      }
      state.viewPly = null;
      clearAnalysis(state);
      return "Move taken back.";
    }
    case "game.resign": {
      const game = requireGame(state);
      if (game.mode !== "play" || game.result !== "*")
        throw new Error("There is no active opponent game to resign.");
      game.result = game.humanColor === "w" ? "0-1" : "1-0";
      game.endReason = `${colorName(opposite(game.humanColor))} wins by resignation`;
      game.clock.startedAt = null;
      game.revision++;
      return `${game.endReason}. The game is saved for review.`;
    }
    case "game.claimDraw": {
      const game = requireGame(state);
      if (
        game.result !== "*" ||
        (game.mode === "play" && replay(game).turn() !== game.humanColor) ||
        !positionStatus(replay(game)).claimable
      )
        throw new Error("A draw cannot be claimed in this position.");
      game.result = "1/2-1/2";
      game.endReason = "Draw claimed by repetition or the 50-move rule";
      game.clock.startedAt = null;
      game.revision++;
      return game.endReason;
    }
    case "game.load": {
      const game = a.gameId
        ? state.games.find((g) => g.id === a.gameId)
        : [...state.games].sort((a, b) => b.updatedAt - a.updatedAt)[0];
      if (!game) {
        state.workspace = "library";
        return "You don’t have a saved game yet. Play a game or import a PGN to begin.";
      }
      if (a.ply !== undefined && a.ply > game.moves.length)
        throw new Error("That move does not exist.");
      state.activeGameId = game.id;
      state.activeVariationId = null;
      state.viewPly = a.ply ?? null;
      state.workspace = "board";
      state.lesson = null;
      clearAnalysis(state);
      return `Opened “${game.title}”${a.ply !== undefined ? ` at ply ${a.ply}` : ""}.`;
    }
    case "game.search": {
      const { type, ...filter } = a;
      Object.assign(state.libraryFilter, filter);
      state.workspace = "library";
      return "Showing games that match your filters.";
    }
    case "game.import": {
      const chess = new Chess();
      try {
        chess.loadPgn(a.pgn);
      } catch {
        throw new Error(
          "That PGN contains an invalid position or move. Please check the file.",
        );
      }
      const history = chess.history({ verbose: true });
      if (history.length > 1000)
        throw new Error("Please import a game with at most 1,000 half-moves.");
      const headers = chess.getHeaders();
      const game = createGame(
        state,
        Action.parse({
          type: "game.create",
          fen: history[0]?.before ?? chess.fen(),
          humanColor: a.humanColor,
        }) as Extract<ActionType, { type: "game.create" }>,
      );
      game.mode = "import";
      game.moves = history.map(uci);
      game.headers = Object.fromEntries(
        Object.entries(headers).filter(
          ([k, v]) => k.length < 40 && v.length < 500,
        ),
      );
      game.title =
        headers.White && headers.Black
          ? `${headers.White} · ${headers.Black}`.slice(0, 200)
          : "Imported game";
      game.result = ["1-0", "0-1", "1/2-1/2"].includes(headers.Result)
        ? headers.Result
        : "*";
      if (game.result !== "*") game.endReason = "Imported result";
      finishPosition(game);
      if (headers.Date && /^\d{4}\.\d{2}\.\d{2}$/.test(headers.Date)) {
        const date = new Date(headers.Date.replaceAll(".", "-")).getTime();
        if (Number.isFinite(date)) game.createdAt = date;
      }
      return `Imported ${history.length} half-moves. Comments and side variations are not imported. The main line is ready to explore.`;
    }
    case "game.seek": {
      const { record } = activeContext(state);
      if (!record || (a.ply !== null && a.ply > record.moves.length))
        throw new Error("That move does not exist.");
      state.viewPly = a.ply;
      clearAnalysis(state);
      return a.ply === null
        ? "Back at the live position."
        : `Viewing ply ${a.ply}.`;
    }
    case "position.analyze": {
      guardAnalysis(state);
      const { game, variation, record, chess } = activeContext(state);
      if (!game || !record) throw new Error("Open a game first.");
      if (a.target === "last-move") {
        const referenced = [...state.messages]
          .reverse()
          .find(
            (m) =>
              m.reference?.gameId === game.id &&
              (m.reference.variationId ?? null) === (variation?.id ?? null),
          )?.reference?.ply;
        const latestHumanPly =
          game.mode === "play" &&
          game.moves.length &&
          replay(game).turn() === game.humanColor
            ? game.moves.length - 1
            : game.moves.length;
        const ply =
          a.ply ??
          state.viewPly ??
          (variation ? record.moves.length : (referenced ?? latestHumanPly));
        const reviewState = variation ? structuredClone(state) : state;
        if (variation) reviewState.preferences.memory = false;
        const evidence = await inspectMove(
          reviewState,
          { ...game, initialFen: record.initialFen, moves: record.moves },
          ply,
          provider,
          signal,
        );
        if (!evidence)
          throw new Error("The engine could not complete this review.");
        state.analysis = {
          fen: evidence.fen,
          gameId: game.id,
          revision: record.revision,
          variationId: variation?.id ?? null,
          lines: [evidence.before],
          engine: "Stockfish 19 lite",
          at: now(),
        };
        const reply = evidenceText(evidence, game.hintFirst);
        message(state, reply, "assistant", {
          gameId: game.id,
          ply,
          ...(variation ? { variationId: variation.id } : {}),
        });
        return reply;
      }
      const result = await provider.analyze({
        initialFen: record.initialFen,
        moves: record.moves.slice(0, state.viewPly ?? record.moves.length),
        multiPV: 3,
        milliseconds: 900,
        signal,
      });
      state.analysis = {
        fen: chess.fen(),
        gameId: game.id,
        revision: record.revision,
        variationId: variation?.id ?? null,
        lines: result.lines,
        engine: result.engine,
        at: now(),
      };
      return `Position analyzed with ${result.engine}, depth ${result.lines[0]?.depth ?? 0}. Evaluation is from White’s perspective; this is a limited search.`;
    }
    case "variation.create": {
      guardAnalysis(state);
      const { game, record } = activeContext(state);
      if (!game || !record) throw new Error("Open a game first.");
      const ply = a.ply ?? state.viewPly ?? record.moves.length;
      if (ply > record.moves.length)
        throw new Error("That position does not exist.");
      const chess = replay(record, ply);
      const variation = {
        id: uid(state),
        gameId: game.id,
        sourcePly: ply,
        title: a.title,
        initialFen: chess.fen(),
        moves: [] as string[],
        revision: 0,
        pinned: false,
      };
      for (const move of a.moves)
        variation.moves.push(uci(chess.move(resolveMove(chess, move))));
      state.variations.push(variation);
      state.activeVariationId = variation.id;
      state.viewPly = null;
      state.workspace = "board";
      clearAnalysis(state);
      return "What-If Lab opened. Your actual game is unchanged. Move either side to explore.";
    }
    case "variation.return":
      state.activeVariationId = null;
      state.viewPly = null;
      state.workspace = "board";
      clearAnalysis(state);
      return "Back to your game. Its moves are unchanged.";
    case "variation.load": {
      const variation = state.variations.find((v) => v.id === a.variationId);
      if (!variation) throw new Error("That branch is unavailable.");
      state.activeVariationId = variation.id;
      state.activeGameId = variation.gameId;
      state.viewPly = null;
      state.workspace = "board";
      clearAnalysis(state);
      return `Opened “${variation.title}”.`;
    }
    case "variation.configure": {
      const variation = state.variations.find(
        (item) => item.id === (a.variationId ?? state.activeVariationId),
      );
      if (!variation) throw new Error("Open a branch first.");
      if (a.title !== undefined) variation.title = a.title;
      if (a.pinned !== undefined) variation.pinned = a.pinned;
      return "Branch updated.";
    }
    case "board.annotate": {
      guardAnalysis(state);
      const { chess } = activeContext(state);
      state.annotations = {
        fen: chess.fen(),
        arrows: a.arrows,
        squares: a.squares,
      };
      return "Board annotations updated.";
    }
    case "lesson.compose": {
      guardAnalysis(state);
      const game = requireGame(state);
      state.teaching = {
        gameId: game.id,
        title: a.title,
        text: a.text,
        question: a.question,
      };
      state.lesson = null;
      state.workspace = "lesson";
      return "The explanation is attached to your interactive board.";
    }
    case "variation.compare": {
      guardAnalysis(state);
      const { chess } = activeContext(state);
      state.comparison = [];
      for (const input of a.moves) {
        const move = resolveMove(chess, input);
        const copy = new Chess(chess.fen());
        const san = copy.move(move).san;
        const result = await provider.analyze({
          initialFen: chess.fen(),
          moves: [move],
          milliseconds: 700,
          signal,
        });
        if (!result.lines[0])
          throw new Error("The engine could not compare those moves.");
        state.comparison.push({ move: san, line: result.lines[0] });
      }
      state.workspace = "compare";
      return "Candidate moves compared in isolated lines. Your game is unchanged.";
    }
    case "coach.configure": {
      const { type, mode, liveReview, ...settings } = a;
      Object.assign(state.preferences, settings);
      if (liveReview !== undefined) state.preferences.liveReview = liveReview;
      if ((mode === "quiet" || mode === "postgame") && liveReview === undefined)
        state.preferences.liveReview = "off";
      if (mode) state.preferences.coaching = mode;
      if (
        liveReview === "every-move" &&
        !mode &&
        ["quiet", "postgame"].includes(state.preferences.coaching)
      )
        state.preferences.coaching = "active";
      const game = state.games.find((g) => g.id === state.activeGameId);
      if (game) {
        if (mode) game.coaching = mode;
        if (a.hintFirst !== undefined) game.hintFirst = a.hintFirst;
        if (liveReview !== undefined) {
          if (game.liveReview !== liveReview) {
            game.liveReviewFrom = game.moves.length;
            for (const variation of state.variations.filter(
              (v) => v.gameId === game.id,
            ))
              variation.liveReviewFrom = variation.moves.length;
          }
          game.liveReview = liveReview;
          if (
            liveReview === "every-move" &&
            !mode &&
            ["quiet", "postgame"].includes(game.coaching)
          )
            game.coaching = "active";
        }
        if (
          (mode === "quiet" || mode === "postgame") &&
          liveReview === undefined
        )
          game.liveReview = "off";
      }
      if (mode === "postgame") clearAnalysis(state);
      return "Coaching preferences saved.";
    }
    case "lesson.create": {
      const lesson = lessons[a.topic];
      const game = createGame(
        state,
        Action.parse({ type: "game.create", fen: lesson.fen }) as Extract<
          ActionType,
          { type: "game.create" }
        >,
      );
      game.title = lesson.subtitle;
      state.lesson = a.topic;
      state.workspace = "lesson";
      return `${lesson.text}\n\n${lesson.question}`;
    }
    case "practice.create": {
      const exercises: Exercise[] = [];
      if (a.source === "personal") {
        const evidence = state.evidence
          .filter((e) => !e.dismissed)
          .sort((a, b) => b.loss - a.loss)
          .slice(0, 6);
        if (!evidence.length) {
          state.workspace = "practice";
          state.practice = null;
          return "There are no reviewed mistakes to build on yet. Review a saved game, or start clearly labeled foundation practice.";
        }
        for (const e of evidence) {
          const concept =
            e.pattern === "allowed-mate"
              ? "Look for the opponent’s forcing threats. Which candidate best addresses them?"
              : "Compare checks, captures and threats. Look for the strongest reply to each candidate.";
          exercises.push(
            await makeExercise(
              state,
              {
                fen: e.fen,
                title: patternLabels[e.pattern],
                concept,
                source: "personal",
                evidenceId: e.id,
                gameId: e.gameId,
                variant: "original",
              },
              provider,
              signal,
            ),
          );
          const transformed = mirrorPerspective(e.fen);
          const mappedMove = mirrorMove(e.best);
          const verified = await provider.analyze({
            initialFen: transformed,
            milliseconds: 600,
            multiPV: 1,
            signal,
          });
          // Only retain a transformed exercise when the same move idea survives engine verification.
          if (verified.bestMove === mappedMove)
            exercises.push(
              await makeExercise(
                state,
                {
                  fen: transformed,
                  title: "The same decision, another perspective",
                  concept,
                  source: "personal",
                  evidenceId: e.id,
                  gameId: e.gameId,
                  variant: "perspective",
                },
                provider,
                signal,
              ),
            );
        }
      } else if (a.source === "current") {
        guardAnalysis(state);
        const { game, chess } = activeContext(state);
        exercises.push(
          await makeExercise(
            state,
            {
              fen: chess.fen(),
              title: "A decision from your board",
              concept:
                "Name two candidate moves. Check the opponent’s forcing replies.",
              source: "personal",
              gameId: game?.id,
            },
            provider,
            signal,
          ),
        );
      } else
        for (const item of curriculum)
          exercises.push(
            await makeExercise(
              state,
              { ...item, source: "curriculum" },
              provider,
              signal,
            ),
          );
      exercises.sort((a, b) => a.dueAt - b.dueAt);
      state.practice = {
        exerciseIds: exercises.map((e) => e.id),
        index: 0,
        hintLevel: 0,
        solved: false,
        feedback: "",
        attempts: 0,
      };
      state.workspace = "practice";
      return `${exercises.length} engine-validated exercises are ready. ${a.source === "curriculum" ? "These are authored foundation positions, not your personal history." : "Each position links back to your actual board or reviewed game."}`;
    }
    case "practice.start": {
      if (!state.exercises.some((exercise) => exercise.id === a.exerciseId))
        throw new Error("That exercise is unavailable.");
      state.practice = {
        exerciseIds: [a.exerciseId],
        index: 0,
        hintLevel: 0,
        solved: false,
        feedback: "",
        attempts: 0,
      };
      state.workspace = "practice";
      return "Exercise opened.";
    }
    case "practice.evaluate": {
      const session = state.practice;
      const exercise = state.exercises.find(
        (e) => e.id === session?.exerciseIds[session.index],
      );
      if (!session || !exercise) throw new Error("Start an exercise first.");
      if (session.solved)
        throw new Error("This exercise is complete. Continue to the next one.");
      const chess = new Chess(exercise.fen);
      const move = resolveMove(chess, a.move);
      let correct = move === exercise.solution[0];
      if (!correct) {
        const [best, alternative] = await Promise.all([
          provider.analyze({
            initialFen: exercise.fen,
            moves: [exercise.solution[0]],
            milliseconds: 600,
            signal,
          }),
          provider.analyze({
            initialFen: exercise.fen,
            moves: [move],
            milliseconds: 600,
            signal,
          }),
        ]);
        if (best.lines[0] && alternative.lines[0])
          correct =
            score(best.lines[0], chess.turn()) -
              score(alternative.lines[0], chess.turn()) <=
            35;
      }
      const outcome = correct
        ? session.hintLevel >= 3
          ? "revealed"
          : session.hintLevel || session.attempts
            ? "hinted"
            : "independent"
        : "incorrect";
      exercise.attempts.push({ at: now(), outcome, move });
      session.attempts++;
      session.solved = correct;
      exercise.dueAt = now() + (outcome === "independent" ? 3 : 1) * 86400000;
      session.feedback = correct
        ? `${chess.move(move).san} works in this engine search. ${outcome === "independent" ? "Solved independently. Retest scheduled in three days." : "Completed with assistance. We’ll revisit this tomorrow."}`
        : "There is a stronger continuation in this search. Before trying again, check your opponent’s forcing reply.";
      return session.feedback;
    }
    case "practice.hint": {
      const session = state.practice;
      const exercise = state.exercises.find(
        (e) => e.id === session?.exerciseIds[session.index],
      );
      if (!session || !exercise) throw new Error("Start an exercise first.");
      session.hintLevel = Math.min(3, session.hintLevel + 1);
      session.feedback =
        session.hintLevel === 1
          ? exercise.concept
          : session.hintLevel === 2
            ? `Look at the piece on ${exercise.solution[0].slice(0, 2)}. Where can it be most effective?`
            : `The engine’s preferred move is ${exercise.san[0]}. Play it to complete this exercise with the answer revealed.`;
      return session.feedback;
    }
    case "practice.next": {
      const session = state.practice;
      if (!session) throw new Error("Start practice first.");
      session.index++;
      session.hintLevel = 0;
      session.solved = false;
      session.feedback = "";
      session.attempts = 0;
      return session.index >= session.exerciseIds.length
        ? "Session complete. Your results and retest dates are saved."
        : "Next exercise ready.";
    }
    case "trainingPlan.create": {
      if (!state.exercises.length)
        await executeOne(
          state,
          Action.parse({
            type: "practice.create",
            source: state.evidence.some((e) => !e.dismissed)
              ? "personal"
              : "curriculum",
          }),
          provider,
          signal,
        );
      const exercises = state.exercises
        .filter(
          (e) =>
            !e.evidenceId ||
            state.evidence.some(
              (observation) =>
                observation.id === e.evidenceId && !observation.dismissed,
            ),
        )
        .sort(
          (a, b) =>
            (a.source === "personal" ? 0 : 1) -
              (b.source === "personal" ? 0 : 1) || a.dueAt - b.dueAt,
        )
        .slice(0, 3);
      const plan = {
        id: uid(state),
        title: a.title,
        minutes: a.minutes,
        items: exercises.map((e, i) => ({
          title: e.title,
          minutes:
            Math.floor(a.minutes / exercises.length) +
            (i < a.minutes % exercises.length ? 1 : 0),
          exerciseIds: [e.id],
          done: false,
        })),
      };
      state.plans.unshift(plan);
      state.activePlanId = plan.id;
      state.workspace = "plan";
      return `Your ${a.minutes}-minute plan is ready, with working exercises and editable time blocks.`;
    }
    case "trainingPlan.update": {
      const plan = state.plans.find((p) => p.id === a.planId);
      const item = plan?.items[a.item];
      if (!plan || !item) throw new Error("That plan item is unavailable.");
      if (a.done !== undefined) item.done = a.done;
      if (a.minutes) item.minutes = a.minutes;
      if (a.title !== undefined) item.title = a.title;
      plan.minutes = plan.items.reduce((sum, i) => sum + i.minutes, 0);
      return "Training plan updated.";
    }
    case "memory.update": {
      const evidence = state.evidence.find((e) => e.id === a.evidenceId);
      if (!evidence) throw new Error("That observation is unavailable.");
      if (a.dismissed !== undefined) evidence.dismissed = a.dismissed;
      if (a.note !== undefined) evidence.note = a.note;
      return "Your correction is saved and will influence future practice.";
    }
    case "studio.create": {
      let description = "";
      let question = "";
      let title = "";
      let game: Game;
      const references: string[] = [];
      if (a.kind === "opening") {
        const opening = a.opening ?? "italian";
        const idea = openingIdeas[opening];
        game = createGame(
          state,
          Action.parse({ type: "game.create", opening }) as Extract<
            ActionType,
            { type: "game.create" }
          >,
        );
        game.pinned = true;
        title = idea.title;
        description = idea.text;
        question = idea.question;
        game.headers.Study = "Opening repertoire";
      } else if (a.kind === "conversion" || a.kind === "defense") {
        const fen = "8/5pk1/6p1/8/4P3/5KP1/8/R6r w - - 0 1";
        const result = await provider.analyze({
          initialFen: fen,
          milliseconds: 800,
          signal,
        });
        game = createGame(
          state,
          Action.parse({
            type: "game.create",
            mode: "play",
            fen,
            humanColor: a.kind === "conversion" ? "w" : "b",
            difficulty: "balanced",
            coaching: "guided",
          }) as Extract<ActionType, { type: "game.create" }>,
        );
        title =
          a.kind === "conversion"
            ? "Make the extra pawn matter"
            : "Find a way to resist";
        description = `A rook ending with an extra white pawn. ${a.kind === "conversion" ? "Activate your king and rook before pushing automatically." : "Keep your rook active and look for counterplay."} Stockfish checked this starting position at depth ${result.lines[0]?.depth ?? 0}; no tablebase result is claimed.`;
        question =
          a.kind === "conversion"
            ? "Which piece can improve without creating a weakness?"
            : "Can you make the opponent answer a threat before improving their position?";
      } else if (a.kind === "preparation") {
        if (!a.opponent?.trim())
          throw new Error(
            "Which opponent name should I look for in your saved games?",
          );
        const matches = state.games
          .filter(
            (g) =>
              [g.headers.White, g.headers.Black].some((name) =>
                name?.toLowerCase().includes(a.opponent!.toLowerCase()),
              ) && g.moves.length,
          )
          .slice(0, 20);
        if (!matches.length)
          throw new Error(
            "No saved games match that opponent. Import completed PGNs with the player names first.",
          );
        const source = matches[0];
        references.push(...matches.map((g) => g.id));
        const ply = Math.min(8, source.moves.length);
        const fen = replay(source, ply).fen();
        const opponentColor = source.headers.White?.toLowerCase().includes(
          a.opponent.toLowerCase(),
        )
          ? "w"
          : "b";
        game = createGame(
          state,
          Action.parse({
            type: "game.create",
            mode: "play",
            fen,
            aiColor: opponentColor,
            difficulty: "balanced",
          }) as Extract<ActionType, { type: "game.create" }>,
        );
        title = `Prepare for ${a.opponent}`;
        description = `Found ${matches.length} saved games matching this player. This training game starts after ${ply} half-moves of “${source.title}”. The opponent is Stockfish, not a calibrated simulation or a prediction of that player.`;
        const openings = Object.entries(
          matches.reduce<Record<string, number>>((counts, g) => {
            const label =
              g.headers.Opening ?? g.headers.ECO ?? "Unlabeled opening";
            counts[label] = (counts[label] ?? 0) + 1;
            return counts;
          }, {}),
        )
          .map(([name, count]) => `${name}: ${count}`)
          .join("; ");
        description += ` Observed opening metadata: ${openings}.`;
        question =
          "What plan fits the position you actually reached in these games?";
      } else {
        guardAnalysis(state);
        game = requireGame(state);
        title =
          a.kind === "calculation"
            ? "Calculate before you move."
            : "Make the idea your own.";
        description =
          a.kind === "calculation"
            ? "Enter a candidate move and predicted replies without changing the board. Cretchess checks legality and compares your decision with Stockfish, then you can explore the line."
            : "Explain the position in your own words. Your coach can compare your explanation with this exact board and verified engine findings.";
        question =
          a.kind === "calculation"
            ? "What is your candidate, and what is the opponent’s strongest reply?"
            : "What changed in this position, and why does it matter?";
      }
      if (a.kind !== "calculation" && a.kind !== "teachback")
        game.title = title;
      state.studio = {
        kind: a.kind,
        title,
        description,
        question,
        gameId: game.id,
        attempts: [],
        referenceGameIds: references,
      };
      state.workspace = "board";
      state.lesson = null;
      return `${description} ${question}`;
    }
    case "calculation.evaluate": {
      guardAnalysis(state);
      const { game, chess } = activeContext(state);
      if (
        !game ||
        state.studio?.kind !== "calculation" ||
        state.studio.gameId !== game.id
      )
        throw new Error("Open the calculation studio first.");
      const initial = chess.fen();
      const san: string[] = [];
      const moves: string[] = [];
      for (const input of a.moves) {
        const move = chess.move(resolveMove(chess, input));
        moves.push(uci(move));
        san.push(move.san);
      }
      const [baseline, chosen] = await Promise.all([
        provider.analyze({ initialFen: initial, milliseconds: 700, signal }),
        provider.analyze({
          initialFen: initial,
          moves: [moves[0]],
          milliseconds: 700,
          signal,
        }),
      ]);
      if (!baseline.lines[0] || !chosen.lines[0])
        throw new Error("The engine could not check this calculation.");
      const color = new Chess(initial).turn();
      const loss =
        score(baseline.lines[0], color) - score(chosen.lines[0], color);
      const feedback = `Your line ${san.join(" ")} is legal. ${loss <= 50 ? "Your candidate is close to the engine’s preference in this search." : "The engine prefers a different candidate in this limited search."} After ${san[0]}, the engine’s reply sequence is ${chosen.lines[0].san.slice(0, 5).join(" ") || "no further move (the game has ended)"}. Depth ${chosen.lines[0].depth}. Your active game is unchanged.`;
      state.studio.attempts.push({ line: moves, feedback, at: now() });
      return feedback;
    }
    case "review.create": {
      const game = a.gameId
        ? state.games.find((g) => g.id === a.gameId)
        : requireGame(state);
      if (!game) throw new Error("That game is unavailable.");
      if (
        game.mode === "play" &&
        game.result === "*" &&
        game.coaching === "postgame"
      )
        throw new Error("Review will be available after this game ends.");
      let reviewed = 0;
      for (let ply = 1; ply <= game.moves.length; ply++) {
        signal?.throwIfAborted();
        if (replay(game, ply - 1).turn() !== game.humanColor) continue;
        await inspectMove(state, game, ply, provider, signal);
        reviewed++;
      }
      state.workspace = "memory";
      return `Reviewed ${reviewed} of your moves. ${state.evidence.filter((e) => e.gameId === game.id && !e.dismissed).length} positions currently support practice observations. These are limited engine searches, not a measure of your overall accuracy.`;
    }
  }
}
export async function opponentMove(
  source: AppState,
  gameId: string,
  revision: number,
  provider: EngineProvider,
  signal?: AbortSignal,
) {
  const state = structuredClone(source);
  const game = state.games.find((g) => g.id === gameId);
  if (
    !game ||
    game.revision !== revision ||
    game.mode !== "play" ||
    game.result !== "*"
  )
    throw new Error("This opponent request no longer applies.");
  const chess = replay(game);
  if (chess.turn() === game.humanColor) throw new Error("It is your turn.");
  if (tickClock(game)) return state;
  const result = await provider.analyze({
    initialFen: game.initialFen,
    moves: game.moves,
    milliseconds:
      game.difficulty === "gentle"
        ? 150
        : game.difficulty === "balanced"
          ? 350
          : 800,
    difficulty: game.difficulty,
    signal,
  });
  if (!result.bestMove) {
    finishPosition(game);
    return state;
  }
  const saved = {
    gameId: state.activeGameId,
    variationId: state.activeVariationId,
    ply: state.viewPly,
  };
  state.activeGameId = game.id;
  state.activeVariationId = null;
  state.viewPly = null;
  applyMove(state, result.bestMove, true);
  state.activeGameId = saved.gameId;
  state.activeVariationId = saved.variationId;
  state.viewPly = saved.ply;
  return state;
}
export { evidenceText, gamePgn };
